import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Advice } from '@Sage/hoi-poi-ui';
import { getDecimalsByConfig, DECIMAL_TYPES } from 'utils/decimal';
import { normalizeEnvironmentValue } from 'utils/multiEnvironment';

import FieldWrapper from '../Fields/FieldWrapper';

// Field Components
import BoolField from '../Fields/Bool';
import CheckboxGroup from '../Fields/CheckboxGroup';
import CurrencyField from '../Fields/Currency';
import DateField from '../Fields/Date';
import DateRangeField from '../Fields/DateRange';
import DateTimeField from '../Fields/DateTime';
import DecimalField from '../Fields/Decimal';
import IntegerField from '../Fields/Integer';
import MultiSelectField from '../Fields/MultiSelect';
import MultiSelectFuzzy from '../Fields/MultiSelectFuzzy';
import PercentField from '../Fields/Percent';
import RichTextField from '../Fields/RichText';
import SelectCustom from '../Fields/SelectCustom';
import SelectFamilyModal from '../Fields/SelectFamilyModal';
import SelectField from '../Fields/Select';
import SelectFuzzy from '../Fields/SelectFuzzy';
import SizeField from '../Fields/Size';
import TextField from '../Fields/Text';
import TextareaField from '../Fields/Textarea';
import TranslationsField from '../Fields/Translations';

// Field Type Map
const FieldTypeMap = {
    // Text inputs
    text: TextField,
    url: TextField,

    // Numeric inputs
    number: IntegerField,
    integer: IntegerField,
    decimal: DecimalField,
    currency: CurrencyField,

    // Date/Time inputs
    date: DateField,
    dateTime: DateTimeField,
    dateRange: DateRangeField,

    // Boolean inputs
    bool: BoolField,

    // Select inputs (single)
    select: SelectField,
    selectCustom: SelectCustom,
    singleValueList: SelectField,
    fuzzySearchSingle: SelectFuzzy,

    // Select inputs (multiple)
    multiSelect: MultiSelectField,
    multipleValueList: MultiSelectField,
    fuzzySearchMultiple: MultiSelectFuzzy,

    // Textarea
    textarea: TextareaField,
    richtext: RichTextField,

    // Special inputs
    size: SizeField,
    checkboxGroup: CheckboxGroup,
    percent: PercentField,
    selectFamilyModal: SelectFamilyModal,
    translations: TranslationsField,
};

const CrudField = memo(
    ({
        actions,
        autoFocus,
        canClear,
        className,
        component,
        dependenceFilter,
        description,
        duplicates,
        error,
        fakeError,
        fieldExtraValue,
        fieldId,
        firstErrorField,
        hidden,
        hint,
        idCurrency,
        idEnvironment,
        includeTime,
        inputAttrs,
        inputFields,
        isBulkAction,
        isNewEntity,
        label,
        labelMode,
        limitInput,
        mandatory,
        maxLength,
        menuProps,
        onChange,
        onClear,
        parentValue,
        placeholderReadOnly,
        readOnly,
        spaced = false,
        type,
        toDelete,
        value,
        willBeCleared,
        workFlow,
        environmentField,
        preAdvice,
        entityName,
        fieldConfiguration,
        isEnriched = false,
    }) => {
        const fieldRef = useRef(null);
        const [isHiddenByField, setIsHiddenByField] = useState(false);

        // Callback for fields to notify they should be hidden (e.g., hideIfEmptyList)
        const handleHideChange = useCallback((shouldHide) => {
            setIsHiddenByField(shouldHide);
        }, []);

        // Scroll into view when first error field
        useEffect(() => {
            if (firstErrorField && fieldRef.current) {
                fieldRef.current.scrollIntoView({ behavior: 'smooth' });
            }
        }, [firstErrorField]);

        // Select the correct field component (dateTime without includeTime → date only)
        const Field = useMemo(() => {
            if (component) return component;
            if (type === 'dateTime' && !includeTime) return FieldTypeMap.date;
            return FieldTypeMap[type] || null;
        }, [component, type, includeTime]);

        // Calculate duplicate state
        const { isDuplicate, isDuplicateLoading } = useMemo(() => {
            let duplicate = false;
            let loading = false;

            if (duplicates?.toasts && inputAttrs?.checkDuplicateId) {
                duplicate = !!duplicates.toasts[inputAttrs.checkDuplicateId];
                if (duplicates.loading && duplicates.currentField === inputAttrs.checkDuplicateId) {
                    loading = true;
                }
            }

            return { isDuplicate: duplicate, isDuplicateLoading: loading };
        }, [duplicates, inputAttrs?.checkDuplicateId]);

        // Fields that handle their own internal error display (like dateRange with sub-field errors)
        // text/textarea/url handle maxLength validation internally
        const fieldsWithInternalErrorMessage = ['dateRange', 'text', 'textarea', 'url'];
        const shouldPassFullErrorToField = fieldsWithInternalErrorMessage.includes(type);

        // Calculate final error
        const finalError = useMemo(() => {
            if (error) return error;
            if (fakeError) return fakeError({ value, fieldId });
            return null;
        }, [error, fakeError, value, fieldId]);

        // Convert object errors to boolean (fields like dateRange handle their own error display)
        // For fields with internal error display (text, textarea, url), don't pass error to wrapper
        const wrapperError = useMemo(() => {
            if (!finalError) return null;
            // Fields that display their own error text shouldn't have error in wrapper
            if (shouldPassFullErrorToField) {
                return null;
            }
            // If error is an object with keys, return true (field handles its own error display)
            if (typeof finalError === 'object' && !Array.isArray(finalError)) {
                return Object.keys(finalError).length > 0 ? true : null;
            }
            return finalError;
        }, [finalError, shouldPassFullErrorToField]);

        // Error to pass to field component:
        // - Fields with internal error message get the full error
        // - Other fields get boolean to show error styling (red border)
        const fieldError = useMemo(() => {
            if (!finalError) return undefined;
            if (shouldPassFullErrorToField) return finalError;
            // Pass boolean for other fields to show error styling
            return !!finalError;
        }, [finalError, shouldPassFullErrorToField]);

        // Calculate class name
        const crudFieldClassName = useMemo(() => {
            return classnames('fm-crud-field', {
                [`fm-crud-field__${fieldId}`]: !!fieldId,
                'fm-crud-field--spaced': spaced,
            });
        }, [fieldId, spaced]);

        // Remove actions from inputAttrs to prevent overwriting the transformed actions
        const cleanInputAttrs = useMemo(() => {
            if (!inputAttrs) return {};
            const { actions: _actions, ...rest } = inputAttrs;
            return rest;
        }, [inputAttrs]);

        const limitDecimals = useMemo(() => {
            if (type !== 'decimal' && type !== 'currency') return undefined;

            let decimalType;
            if (type === 'currency') {
                decimalType = inputAttrs?.decimalType || DECIMAL_TYPES.CURRENCY_GENERAL;
            } else {
                decimalType = inputAttrs?.decimalType || DECIMAL_TYPES.GENERAL;
            }

            // Use idCurrency and idEnvironment from props (passed from parent) or fallback to inputAttrs
            const finalIdCurrency = idCurrency || inputAttrs?.idCurrency;
            const finalIdEnvironment = normalizeEnvironmentValue(
                idEnvironment || environmentField?.value,
            );

            return getDecimalsByConfig({
                type: decimalType,
                idEnvironment: finalIdEnvironment,
                idCurrency: finalIdCurrency,
                entityName,
                fieldId: fieldConfiguration || fieldId,
            });
        }, [
            type,
            inputAttrs,
            idCurrency,
            idEnvironment,
            environmentField,
            entityName,
            fieldId,
            fieldConfiguration,
        ]);

        const fieldIsEnriched = Boolean(
            isEnriched && !hidden && !inputAttrs?.hidden && !readOnly && !toDelete,
        );

        const isFieldFromMap = !component && FieldTypeMap[type];

        // Reset handler for bulk mode cancel
        const handleReset = useCallback(() => {
            onChange?.(null);
        }, [onChange]);

        if (!Field) return null;

        // Check hidden before rendering wrapper (both prop and inputAttrs.hidden)
        if (isFieldFromMap && (hidden || inputAttrs?.hidden)) return null;

        // Check shouldRenderField before rendering wrapper
        // Standard signature is (fieldId, list, parentField)
        if (isFieldFromMap && typeof inputAttrs?.shouldRenderField === 'function') {
            if (!inputAttrs.shouldRenderField(fieldId, inputAttrs?.list, inputAttrs?.parentField))
                return null;
        }

        // Hide entire wrapper when field requests it (e.g., hideIfEmptyList)
        if (isHiddenByField) return null;

        // Standard fields use FieldWrapper for label, error and bulk logic
        // Custom components handle their own layout
        const fieldComponent = isFieldFromMap ? (
            <FieldWrapper
                canClear={canClear}
                className={className}
                description={description}
                error={wrapperError}
                isBulkAction={isBulkAction}
                isDuplicate={isDuplicate}
                label={label}
                labelMode={labelMode}
                mandatory={mandatory}
                onClear={onClear}
                onReset={handleReset}
                toDelete={toDelete}
                willBeCleared={willBeCleared}
            >
                <Field
                    actions={actions}
                    autoFocus={autoFocus || firstErrorField}
                    canClear={canClear}
                    dependenceFilter={dependenceFilter}
                    environmentField={environmentField}
                    error={fieldError}
                    fieldExtraValue={fieldExtraValue}
                    fieldId={fieldId}
                    firstErrorField={firstErrorField}
                    hasExternalLabel={!!label}
                    hidden={hidden}
                    hint={hint}
                    includeTime={includeTime}
                    inputFields={inputFields}
                    isDuplicate={isDuplicate}
                    isDuplicateLoading={isDuplicateLoading}
                    isNewEntity={isNewEntity}
                    limitInput={limitInput}
                    limitDecimals={limitDecimals}
                    mandatory={mandatory}
                    maxLength={maxLength}
                    menuProps={menuProps}
                    onChange={onChange}
                    onClear={onClear}
                    onHideChange={handleHideChange}
                    parentValue={parentValue}
                    placeholderReadOnly={placeholderReadOnly}
                    readOnly={readOnly || !!toDelete}
                    type={type}
                    value={value}
                    willBeCleared={willBeCleared}
                    workFlow={workFlow}
                    isEnriched={fieldIsEnriched}
                    {...cleanInputAttrs}
                />
            </FieldWrapper>
        ) : (
            <Field
                actions={actions}
                autoFocus={autoFocus || firstErrorField}
                canClear={canClear}
                className={className}
                dependenceFilter={dependenceFilter}
                description={description}
                environmentField={environmentField}
                error={finalError}
                fieldExtraValue={fieldExtraValue}
                fieldId={fieldId}
                firstErrorField={firstErrorField}
                hidden={hidden}
                hint={hint}
                includeTime={includeTime}
                inputFields={inputFields}
                isBulkAction={isBulkAction}
                isDuplicate={isDuplicate}
                isDuplicateLoading={isDuplicateLoading}
                isNewEntity={isNewEntity}
                label={label}
                labelMode={labelMode}
                limitInput={limitInput}
                limitDecimals={limitDecimals}
                mandatory={mandatory}
                maxLength={maxLength}
                menuProps={menuProps}
                onChange={onChange}
                onClear={onClear}
                parentValue={parentValue}
                placeholderReadOnly={placeholderReadOnly}
                readOnly={readOnly}
                type={type}
                value={value}
                willBeCleared={willBeCleared}
                workFlow={workFlow}
                isEnriched={fieldIsEnriched}
                {...cleanInputAttrs}
            />
        );

        return (
            <div ref={fieldRef} className={crudFieldClassName}>
                {preAdvice && (
                    <Advice className="fm-crud-field__pre-advice" {...preAdvice?.props}>
                        {preAdvice?.content}
                    </Advice>
                )}
                {fieldComponent}
            </div>
        );
    },
);

CrudField.displayName = 'CrudField';

CrudField.propTypes = {
    actions: PropTypes.array,
    autoFocus: PropTypes.bool,
    canClear: PropTypes.bool,
    className: PropTypes.string,
    component: PropTypes.elementType,
    dependenceFilter: PropTypes.func,
    description: PropTypes.string,
    duplicates: PropTypes.object,
    environmentField: PropTypes.object,
    entityName: PropTypes.string,
    fieldConfiguration: PropTypes.string,
    error: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    fakeError: PropTypes.func,
    fieldExtraValue: PropTypes.any,
    fieldId: PropTypes.string,
    firstErrorField: PropTypes.bool,
    hidden: PropTypes.bool,
    hint: PropTypes.string,
    idCurrency: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    idEnvironment: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    includeTime: PropTypes.bool,
    inputAttrs: PropTypes.object,
    inputFields: PropTypes.array,
    isBulkAction: PropTypes.bool,
    isNewEntity: PropTypes.bool,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    labelMode: PropTypes.string,
    limitInput: PropTypes.bool,
    mandatory: PropTypes.bool,
    maxLength: PropTypes.number,
    menuProps: PropTypes.object,
    onChange: PropTypes.func,
    onClear: PropTypes.func,
    parentValue: PropTypes.any,
    placeholderReadOnly: PropTypes.string,
    preAdvice: PropTypes.object,
    readOnly: PropTypes.bool,
    spaced: PropTypes.bool,
    toDelete: PropTypes.bool,
    type: PropTypes.string,
    value: PropTypes.any,
    willBeCleared: PropTypes.bool,
    workFlow: PropTypes.object,
    isEnriched: PropTypes.bool,
};

export default CrudField;
